import { useEffect, useRef } from 'react';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ottoTokens.overlayBackdrop,
    backdropFilter: 'blur(2px)',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    paddingBlock: tokens.spacingVerticalXXL,
    paddingInline: tokens.spacingHorizontalXXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
  },
});

/**
 * Full-viewport overlay shown while MSAL verifies the user's session.
 *
 * It keeps the landing content visible (and instantly painted) but blocks
 * interaction with it during the silent-SSO window. This prevents a
 * returning user from starting to read/scroll the landing page and then
 * being abruptly redirected to Home when `ssoSilent` resolves. If the user
 * has no session, the overlay simply disappears and the landing becomes
 * interactive.
 *
 * The verifying window is bounded by `VERIFY_TIMEOUT_MS` in AuthProvider,
 * so this overlay can never trap the page indefinitely.
 */
export const VerifyingAccessOverlay = () => {
  const styles = useStyles();
  const cardRef = useRef<HTMLDivElement>(null);

  // Lock background scroll and make the landing page content inert while the
  // overlay is up so keyboard focus cannot reach controls behind it.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const pageContent = document.getElementById('login-page-content');
    const previousAriaHidden = pageContent
      ? pageContent.getAttribute('aria-hidden')
      : null;
    const hadInert = pageContent?.hasAttribute('inert') ?? false;

    document.body.style.overflow = 'hidden';
    cardRef.current?.focus({ preventScroll: true });
    pageContent?.setAttribute('inert', '');
    pageContent?.setAttribute('aria-hidden', 'true');

    return () => {
      document.body.style.overflow = previousOverflow;
      if (pageContent) {
        if (!hadInert) {
          pageContent.removeAttribute('inert');
        }
        if (previousAriaHidden === null) {
          pageContent.removeAttribute('aria-hidden');
        } else {
          pageContent.setAttribute('aria-hidden', previousAriaHidden);
        }
      }
      if (previousFocus?.isConnected) {
        try {
          previousFocus.focus({ preventScroll: true });
        } catch {
          previousFocus.focus();
        }
      }
    };
  }, []);

  return (
    <div
      className={styles.overlay}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        ref={cardRef}
        className={styles.card}
        tabIndex={-1}
        data-testid="verifying-access-card"
      >
        <Spinner size="large" label="Verifying your access…" />
      </div>
    </div>
  );
};
